<script setup lang="ts">
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import { onClickOutside } from "@vueuse/core"
import { ref } from "vue"
import { BaseModal, Button, Icon } from "@/components/ui"
import { useModal } from "@/composables"

const props = withDefaults(
  defineProps<{
    name: string | symbol
    keepAlive?: boolean
    title?: string
    titleIcon?: IconKeys
    showByDefault?: boolean
  }>(),
  { keepAlive: false, title: "", titleIcon: undefined, showByDefault: false },
)

const emit = defineEmits<{
  (e: "open"): void
  (e: "close"): void
}>()

const drawerRef = ref<HTMLElement | null>(null)
const { isActive, toggle } = useModal(props.name)

onClickOutside(
  drawerRef,
  () => {
    if (!isActive?.value)
      return
    toggle()
  },
  { ignore: ["[data-ignore='toast-root']", ".gpx-toast-container"] },
)
</script>

<template>
  <BaseModal
    :name="name"
    :keep-alive="keepAlive"
    :show-by-default="showByDefault"
    @open="emit('open')"
    @close="emit('close')"
  >
    <aside
      ref="drawerRef"
      :class="{ 'gpx-drawer_active': isActive }"
      :aria-hidden="!isActive"
      :tabindex="!isActive ? -1 : 0"
    >
      <div class="gpx-drawer z-40 flex flex-col h-full">
        <slot name="header">
          <div class="gpx-drawer-header px-6">
            <div class="gpx-drawer-header-title">
              <slot name="titleIcon">
                <Icon v-if="titleIcon" :icon="titleIcon" />
              </slot>
              <h2 class="line-clamp-1 text-ellipsis" data-test="drawer_title">
                {{ title }}
              </h2>
            </div>
            <Button variant="flat" class="gpx-drawer-dismiss" @click="toggle">
              <span class="sr-only">Close</span>
              <Icon icon="Close" />
            </Button>
          </div>
        </slot>

        <div class="grow overflow-y-auto px-6">
          <slot />
        </div>

        <slot name="footer" />
      </div>
    </aside>
  </BaseModal>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css";

.gpx-drawer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  max-height: 75vh;
  border-radius: 12px 12px 0 0;
  box-sizing: border-box;
  overflow-y: auto;

  @apply border-t border-l-0 border-gray-200 bg-white;
}

.gpx-drawer-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  min-height: 3rem;
  padding-top: 1rem;
  position: sticky;
  top: 0;

  @apply text-primary;
}

.gpx-drawer-header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gpx-drawer-header-title h2 {
  @apply font-comfortaa text-2xl;
}

.gpx-drawer-dismiss {
  padding: 0;
}

.gpx-fade-enter-active .gpx-drawer,
.gpx-fade-leave-active .gpx-drawer {
  transition: transform 0.3s ease-in-out;
}

.gpx-fade-enter-active .gpx-drawer {
  transition-delay: 0.15s;
}

.gpx-fade-enter-from .gpx-drawer,
.gpx-fade-leave-to .gpx-drawer {
  transform: translateY(460px);
}

@media (width >=768px) {
  .gpx-drawer {
    top: 0;
    right: 0;
    left: unset;
    width: 460px;
    max-width: 95vw;
    max-height: 100%;
    height: 100%;

    @apply border-l border-t-0 rounded-none;
  }

  .gpx-drawer-header-title h2 {
    font-size: 1.5rem;
  }

  .gpx-fade-enter-from .gpx-drawer,
  .gpx-fade-leave-to .gpx-drawer {
    transform: translateX(460px);
  }
}
</style>
